// Manage (spec 16 §4, §3.12 items 3, 4 and 8). A Modal listing every view on
// the screen in the chip row's order, with the other members' shared views
// under the same "Shared" label as the row. It offers rename, share and
// unshare for the reader's own views, and delete for the reader's own or, for
// a moderator, any shared view that is not a system view. Both answers come
// from the server on each row (`mutable`, `can_moderate`), and both are false
// on a system view, so the switcher reads no permission itself. A row the
// reader may neither change nor delete shows its name alone. Manage holds no
// apply action: applying a view the row does not show is More views' job, at
// every width (D-305 ruling 21).
//
// Manage is absent below 769 px (ruling 20); the switcher decides that.
//
// A rename refused for its NAME (409 `SAVED_VIEW_NAME_TAKEN`, the pipe's 400
// on `name`, or the client's own length check) is a field error bound to the
// rename input through `aria-describedby`, as Save as new binds it (R8, PR-E4
// wave 4). Every other refusal is the row's form line.
//
// Each act a row offers is a write that keeps its Idempotency-Key until the
// server answers it (V2, `../lib/write-key.ts`): a rename from the moment its
// form opens, sharing and unsharing each on their own, and a delete from the
// moment its confirmation opens. Cancelling a rename or a delete abandons that
// write and its key. A delete asks first ("Delete {name}? This cannot be
// undone.", with Delete view and Cancel), and the author's own rows say
// whether the view is Private or Shared (spec 16 §4).

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { nameError, type ViewError } from "../lib/view-errors";
import { viewLabel } from "../lib/view-label";
import type { SavedView, ViewGroups } from "../lib/view-state";
import { WriteKeys, type WriteKey } from "../lib/write-key";
import { ViewErrorLine } from "./view-error-line";

export interface ManageViewsModalProps {
  readonly groups: ViewGroups;
  readonly onClose: () => void;
  readonly rename: (id: string, name: string, idempotencyKey: string) => Promise<void>;
  readonly setShared: (id: string, shared: boolean, idempotencyKey: string) => Promise<void>;
  readonly remove: (id: string, idempotencyKey: string) => Promise<void>;
}

type RowMode = "idle" | "rename" | "confirm";

function ManageRow({
  view,
  rename,
  setShared,
  remove,
  onBusy,
}: {
  view: SavedView;
  rename: (id: string, name: string, idempotencyKey: string) => Promise<void>;
  setShared: (id: string, shared: boolean, idempotencyKey: string) => Promise<void>;
  remove: (id: string, idempotencyKey: string) => Promise<void>;
  onBusy: (busy: boolean) => void;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const [mode, setMode] = useState<RowMode>("idle");
  const [draft, setDraft] = useState(view.name);
  const [busy, setBusyState] = useState(false);
  const [error, setError] = useState<ViewError | undefined>(undefined);
  const [keys] = useState(() => new WriteKeys());
  const label = viewLabel(view, t);
  const nameErrorId = `${inputId}-error`;
  const nameFieldError = mode === "rename" && error?.target === "name" ? error : undefined;
  const formError = nameFieldError === undefined ? error : undefined;
  const own = view.mutable && !view.is_system;
  const canDelete = own || (view.can_moderate && !view.is_system);

  const setBusy = (next: boolean) => {
    setBusyState(next);
    onBusy(next);
  };

  const run = async (
    write: WriteKey,
    act: (idempotencyKey: string) => Promise<void>,
    after?: () => void,
  ) => {
    setBusy(true);
    setError(undefined);
    try {
      await act(write.take());
      write.settle();
      after?.();
    } catch (caught) {
      setError(write.settle(caught));
    } finally {
      setBusy(false);
    }
  };

  const submitRename = () => {
    const local = nameError(draft);
    if (local !== undefined) {
      setError(local);
      return;
    }
    void run(
      keys.for("rename"),
      (key) => rename(view.id, draft, key),
      () => {
        setMode("idle");
      },
    );
  };

  return (
    <div
      role="listitem"
      className="views-row"
      data-testid={`views-manage-row-${view.id}`}
      data-view={view.id}
    >
      {mode === "rename" ? (
        <form
          className="views-row-form"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            submitRename();
          }}
        >
          <Field label={t("views.manage.renameLabel", { name: view.name })}>
            <input
              id={inputId}
              className="ds-input"
              value={draft}
              autoComplete="off"
              aria-invalid={nameFieldError !== undefined}
              {...(nameFieldError !== undefined ? { "aria-describedby": nameErrorId } : {})}
              data-testid="views-manage-rename-input"
              onChange={(event) => {
                setDraft(event.target.value);
                if (error?.target === "name") setError(undefined);
              }}
            />
          </Field>
          {nameFieldError !== undefined ? (
            <p
              id={nameErrorId}
              className="field-error"
              role="alert"
              data-testid="views-manage-rename-error"
            >
              {t(nameFieldError.key, { ...nameFieldError.params })}
            </p>
          ) : null}
          <div className="views-row-actions">
            <Button
              size="sm"
              variant="primary"
              type="submit"
              busy={busy}
              busyLabel={t("views.manage.working")}
              data-testid="views-manage-rename-save"
            >
              {t("views.manage.renameSave")}
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                keys.release("rename");
                setMode("idle");
                setDraft(view.name);
                setError(undefined);
              }}
            >
              {t("views.manage.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <span className="views-row-name">{label}</span>
          {own ? (
            <span className="views-row-meta">
              {view.shared ? t("views.manage.isShared") : t("views.manage.private")}
            </span>
          ) : null}
          {mode === "confirm" ? (
            <div className="views-row-actions">
              <span className="views-row-meta">
                {t("views.manage.deleteConfirm", { name: label })}
              </span>
              <Button
                size="sm"
                variant="danger"
                busy={busy}
                busyLabel={t("views.manage.working")}
                data-testid="views-manage-delete-confirm"
                onClick={() => {
                  void run(keys.for("delete"), (key) => remove(view.id, key));
                }}
              >
                {t("views.manage.deleteYes")}
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  keys.release("delete");
                  setMode("idle");
                }}
              >
                {t("views.manage.cancel")}
              </Button>
            </div>
          ) : own || canDelete ? (
            <div className="views-row-actions">
              {own ? (
                <>
                  <Button
                    size="sm"
                    disabled={busy}
                    data-testid="views-manage-rename"
                    onClick={() => {
                      keys.release("rename");
                      setDraft(view.name);
                      setError(undefined);
                      setMode("rename");
                    }}
                  >
                    {t("views.manage.rename")}
                  </Button>
                  <Button
                    size="sm"
                    busy={busy}
                    busyLabel={t("views.manage.working")}
                    data-testid="views-manage-share"
                    onClick={() => {
                      const next = !view.shared;
                      void run(keys.for(next ? "share" : "unshare"), (key) =>
                        setShared(view.id, next, key),
                      );
                    }}
                  >
                    {view.shared ? t("views.manage.unshare") : t("views.manage.share")}
                  </Button>
                </>
              ) : null}
              {canDelete ? (
                <Button
                  size="sm"
                  disabled={busy}
                  data-testid="views-manage-delete"
                  onClick={() => {
                    keys.release("delete");
                    setError(undefined);
                    setMode("confirm");
                  }}
                >
                  {t("views.manage.delete")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
      {formError !== undefined ? (
        <ViewErrorLine error={formError} testId="views-manage-error" />
      ) : null}
    </div>
  );
}

export function ManageViewsModal({
  groups,
  onClose,
  rename,
  setShared,
  remove,
}: ManageViewsModalProps) {
  const { t } = useTranslation();
  const sharedLabelId = useId();
  const [busyCount, setBusyCount] = useState(0);
  const onBusy = (busy: boolean) => {
    setBusyCount((n) => Math.max(0, n + (busy ? 1 : -1)));
  };
  const rowProps = { rename, setShared, remove, onBusy };

  return (
    <Modal
      label={t("views.manage.title")}
      heading={t("views.manage.title")}
      closeLabel={t("views.manage.close")}
      onClose={onClose}
      pending={busyCount > 0}
      className="views-modal"
      testId="views-manage-modal"
    >
      <div role="list" className="views-list">
        {[...groups.system, ...groups.own].map((view) => (
          <ManageRow key={view.id} view={view} {...rowProps} />
        ))}
      </div>
      {groups.shared.length > 0 ? (
        <div role="group" aria-labelledby={sharedLabelId} data-group="shared">
          <p id={sharedLabelId} className="views-list-label">
            {t("views.shared")}
          </p>
          <div role="list" className="views-list">
            {groups.shared.map((view) => (
              <ManageRow key={view.id} view={view} {...rowProps} />
            ))}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
